import { z } from 'zod';
import type { ParentLink, Partnership, Person } from './types';
import { parseYear } from './types';
import { buildGraph, wouldCycle } from './graph';
import { ulid } from './ulid';
import { LINK_KINDS, parseEmail, parseLink, parsePhone, type LinkKind } from './links';

const ProtoPerson = z.object({
  id: z.string(),
  created: z.number().optional(),
  first: z.string().optional().default(''),
  last: z.string().optional().default(''),
  birthName: z.string().optional().default(''),
  gender: z.enum(['female', 'male', 'other']).or(z.literal('')).optional().default(''),
  born: z.string().optional().default(''),
  birthPlace: z.string().optional().default(''),
  living: z.boolean().optional().default(true),
  died: z.string().optional().default(''),
  restingPlace: z.string().optional().default(''),
  occupation: z.string().optional().default(''),
  residence: z.string().optional().default(''),
  story: z.string().optional().default(''),
  email: z.string().optional().default(''),
  phone: z.string().optional().default(''),
  instagram: z.string().optional().default(''),
  facebook: z.string().optional().default(''),
  linkedin: z.string().optional().default(''),
  twitter: z.string().optional().default(''),
  website: z.string().optional().default(''),
  parents: z.array(z.string()).optional().default([]),
  spouses: z.array(z.string()).optional().default([]),
  photo: z.string().optional(),
  photoData: z.string().optional(),
});

export const BackupSchema = z.object({
  app: z.string(),
  version: z.number().int().min(1).max(2),
  exportedAt: z.string().optional(),
  tree: z.object({
    title: z.string().optional().default('Family tree'),
    meId: z.string().nullish(),
    people: z.record(ProtoPerson),
  }),
  photos: z.record(z.string()).optional().default({}),
});
export type Backup = z.infer<typeof BackupSchema>;

export interface ImportResult {
  title: string;
  meId: string | null;
  people: Person[];
  parentLinks: ParentLink[];
  partnerships: Partnership[];
  /** new person id → data URL */
  photos: Map<string, string>;
  /** prototype person id → new person id */
  idMap: Record<string, string>;
  skipped: { what: string; reason: string }[];
}

export class ImportError extends Error {}

const nz = (s: string | undefined) => (s && s.trim() ? s.trim() : null);

/** Parse a prototype backup (v1 or v2) into Lineage entities with fresh ULIDs. */
export function parseBackup(raw: unknown, now: number = Date.now()): ImportResult {
  if (typeof raw === 'object' && raw !== null && (raw as { app?: unknown }).app !== 'lineage') {
    throw new ImportError(`This file isn't a Lineage backup (app: "${String((raw as { app?: unknown }).app)}").`);
  }
  const parsed = BackupSchema.safeParse(raw);
  if (!parsed.success) {
    throw new ImportError(`Backup file is not valid: ${parsed.error.issues[0]?.message ?? 'unknown error'}`);
  }
  const b = parsed.data;
  const skipped: ImportResult['skipped'] = [];
  const protos = Object.values(b.tree.people).sort(
    (x, y) => (x.created ?? 0) - (y.created ?? 0) || (x.id < y.id ? -1 : 1),
  );
  const idMap = new Map<string, string>();
  const people: Person[] = [];
  const photos = new Map<string, string>();
  protos.forEach((p, i) => {
    if (!p.first.trim() && !p.last.trim()) {
      skipped.push({ what: p.id, reason: 'person has no name' });
      return;
    }
    const id = ulid(now + i);
    idMap.set(p.id, id);
    const who = `${p.first} ${p.last}`.trim();
    const contact = (what: string, r: ReturnType<typeof parseEmail>) => {
      if (r.error !== undefined) skipped.push({ what: `${what} of ${who}`, reason: r.error });
      return r.error === undefined ? r.value : null;
    };
    const links = Object.fromEntries(
      LINK_KINDS.map((k: LinkKind) => [k, contact(k, parseLink(k, p[k]))]),
    ) as Record<LinkKind, string | null>;
    people.push({
      id,
      firstName: nz(p.first),
      lastName: nz(p.last),
      birthName: nz(p.birthName),
      gender: p.gender === '' ? null : p.gender,
      born: nz(p.born),
      bornYear: parseYear(p.born),
      birthPlace: nz(p.birthPlace),
      living: p.living,
      died: nz(p.died),
      diedYear: parseYear(p.died),
      restingPlace: nz(p.restingPlace),
      occupation: nz(p.occupation),
      residence: nz(p.residence),
      story: nz(p.story),
      email: contact('email', parseEmail(p.email)),
      phone: contact('phone', parsePhone(p.phone)),
      ...links,
      photoId: null,
      createdAt: (p.created ?? now) + i * 0, // keep prototype order
    });
    const photo = b.photos[p.id] ?? p.photoData;
    if (photo && photo.startsWith('data:image/')) photos.set(id, photo);
    else if (photo) skipped.push({ what: `photo of ${p.first} ${p.last}`.trim(), reason: 'not an image data URL' });
  });

  const parentLinks: ParentLink[] = [];
  const partnerships: Partnership[] = [];
  const seenPairs = new Set<string>();
  for (const p of protos) {
    const child = idMap.get(p.id);
    if (!child) continue;
    p.parents.slice(0, 2).forEach((pp) => {
      const parent = idMap.get(pp);
      if (!parent) {
        skipped.push({ what: `parent link ${pp} → ${p.id}`, reason: 'parent not found' });
        return;
      }
      const g = buildGraph(people, parentLinks, []);
      if (wouldCycle(g, parent, child)) {
        skipped.push({ what: `parent link ${pp} → ${p.id}`, reason: 'would create a cycle' });
        return;
      }
      if (!parentLinks.some((l) => l.parentId === parent && l.childId === child)) {
        parentLinks.push({ parentId: parent, childId: child, kind: 'birth' });
      }
    });
    if (p.parents.length > 2) skipped.push({ what: `parents of ${p.id}`, reason: 'only the first two parents are kept' });
    for (const sp of p.spouses) {
      const other = idMap.get(sp);
      if (!other) {
        skipped.push({ what: `partnership ${p.id} ↔ ${sp}`, reason: 'partner not found' });
        continue;
      }
      if (other === child) continue;
      const [aId, bId] = child < other ? [child, other] : [other, child];
      const key = `${aId}|${bId}`;
      if (seenPairs.has(key)) continue;
      seenPairs.add(key);
      partnerships.push({ id: ulid(now), aId, bId, status: 'married' });
    }
  }
  const meId = b.tree.meId ? (idMap.get(b.tree.meId) ?? null) : null;
  return { title: b.tree.title, meId, people, parentLinks, partnerships, photos, idMap: Object.fromEntries(idMap), skipped };
}
